import { NAVIGATION_EVENTS } from 'src/lib/trackingEvents';

export type TFooterItem = {
  name: string;
  url: string;

  /** URLs are automatically prefixed with the current locale. This can be optionally overriden.  */
  locale?: string;

  trackingId?: string;
  id?: string;
};

export type TFooterSection = {
  header: string;
  items: TFooterItem[];
};

type TFooterSectionKey =
  | 'products'
  | 'useCases'
  | 'docs'
  | 'aboutUs'
  | 'resources'
  | 'forConsumers'
  | 'forInstitutions';

export type TFooterData = {
  copyrightName: string;
  eeaDisclaimer?: string[];
  /** @deprecated This is only used by the legacy `src/components/footer.jsx` component and is not applicable to new pages. It should be removed with the legacy footer. */
  hideCTAWhen: string[];
  sections: Partial<Record<TFooterSectionKey, TFooterSection>>;
};

export const footerData: TFooterData = {
  copyrightName: 'Plaid Inc.',
  hideCTAWhen: [
    '404',
    '500',
    'account-funding',
    'ach-start',
    'ACH-start',
    'assets',
    'auth',
    'balance',
    'banking',
    'business-brokerage-services',
    'careers',
    'consumer-payments',
    'contact-thanks',
    'contact',
    'customer-stories',
    'global',
    'how-it-works-for-consumers',
    'how-we-handle-data',
    'identity',
    'income',
    'integration',
    'investments',
    'job',
    'lending',
    'liabilities',
    'openings',
    'partners',
    'payment-initiation',
    'personal-finances',
    'plaid-exchange',
    'signup',
    'transactions',
    'use-cases',
    'wealth',
    'what-is-plaid',
    'why-is-plaid-involved',
    'trouble-connecting',
    '/', // The "/" represents homepage
  ],
  sections: {
    products: {
      header: 'Products',
      items: [
        {
          id: 'auth',
          name: 'Auth',
          url: '/products/auth/',
        },
        {
          id: 'identity',
          name: 'Identity',
          url: '/products/identity/',
        },
        {
          id: 'balance',
          name: 'Balance',
          url: '/products/balance/',
        },
        {
          id: 'signal',
          name: 'Signal',
          url: '/products/signal/',
        },
        {
          id: 'transfer',
          name: 'Transfer',
          url: '/products/transfer/',
        },
        {
          id: 'identity-verification',
          name: 'Identity Verification',
          url: '/products/identity-verification/',
        },
        {
          id: 'monitor',
          name: 'Monitor',
          url: '/products/monitor/',
        },
        {
          id: 'transactions',
          name: 'Transactions',
          url: '/products/transactions/',
        },
        {
          id: 'investments',
          name: 'Investments',
          url: '/products/investments/',
        },
        {
          id: 'liabilities',
          name: 'Liabilities',
          url: '/products/liabilities/',
        },
        {
          id: 'enrich',
          name: 'Enrich',
          url: '/products/enrich/',
        },
        {
          id: 'assets',
          name: 'Assets',
          url: '/products/assets/',
        },
        {
          id: 'income',
          name: 'Income',
          url: '/check/income/',
        },
        {
          id: 'underwriting',
          name: 'Underwriting',
          url: '/check/underwriting/',
        },
        {
          id: 'plaid-link',
          name: 'Plaid Link',
          url: '/plaid-link/',
        },
        {
          id: 'layer',
          name: 'Layer',
          url: '/layer/',
        },
        {
          id: 'investments-move',
          name: 'Investments Move',
          url: '/products/investments-move/',
        },
        {
          id: 'protect',
          name: 'Protect',
          url: '/products/protect/',
        },
        {
          id: 'cash-advance-index',
          name: 'Cash Advance Index',
          url: '/products/cash-advance-index/',
        },
        {
          id: 'lend-score',
          name: 'LendScore',
          url: '/check/lendscore/',
        },
        {
          id: 'guarantee',
          name: 'Guarantee',
          url: '/products/guaranteed-payments/',
        },
        {
          id: 'database-auth',
          name: 'Database Auth',
          url: '/products/database-auth/',
        },
      ],
    },
    useCases: {
      header: 'Use cases',
      items: [
        {
          id: 'ai-companies',
          name: 'AI companies',
          url: '/industries/ai/',
        },
        {
          id: 'auto',
          name: 'Auto',
          url: '/industries/auto/',
        },
        {
          id: 'banks-credit-unions',
          name: 'Banks & credit unions',
          url: '/industries/banks-credit-unions/',
        },
        {
          id: 'billing-recurring-payments',
          name: 'Billing & recurring payments',
          url: '/industries/billing-recurring-payments/',
        },
        {
          id: 'business-financial-management',
          name: 'Business financial management',
          url: '/use-cases/business-financial-management/',
        },
        {
          id: 'consumer-lending',
          name: 'Consumer lending',
          url: '/industries/consumer-lending/',
        },
        {
          id: 'earned-wage-access',
          name: 'Earned wage access',
          url: '/use-cases/earned-wage-access/',
        },
        {
          id: 'fraud-risk-prevention',
          name: 'Fraud & risk prevention',
          url: '/use-cases/fraud-and-risk-prevention/',
        },
        {
          id: 'gaming',
          name: 'Gaming',
          url: '/industries/gaming/',
        },
        {
          id: 'govt-public-sector',
          name: 'Government & public sector',
          url: '/industries/government-public-sector/',
        },
        {
          id: 'healthcare',
          name: 'Healthcare',
          url: '/industries/healthcare/',
        },
        {
          id: 'inbound',
          name: 'Inbound bank payments',
          url: '/use-cases/inbound-bank-payments/',
        },
        {
          id: 'insurance',
          name: 'Insurance',
          url: '/industries/insurance/',
        },
        {
          id: 'aml-kyc',
          name: 'KYC & AML compliance',
          url: '/use-cases/kyc-aml-compliance/',
        },
        {
          id: 'mortgage',
          name: 'Mortgage',
          url: '/industries/mortgage/',
        },
        {
          id: 'onboarding-identity-verification',
          name: 'Onboarding & identity verification',
          url: '/use-cases/instant-onboarding-identity-verification/',
        },
        {
          id: 'open-finance',
          name: 'Open finance',
          url: '/use-cases/open-finance/',
        },
        {
          id: 'outbound',
          name: 'Outbound bank payments',
          url: '/use-cases/outbound-bank-payments/',
        },
        {
          id: 'personal-financial-management',
          name: 'Personal financial management',
          url: '/use-cases/personal-financial-insights/',
        },
        {
          id: 'platforms-marketplaces',
          name: 'Platforms & marketplaces',
          url: '/industries/platforms-marketplaces/',
        },
        {
          id: 'property-management',
          name: 'Property management',
          url: '/industries/property-management/',
        },
      ],
    },
    docs: {
      header: 'Developers',
      items: [
        {
          id: 'quickstart',
          name: 'Quickstart',
          url: '/docs/quickstart/',
        },
        {
          id: 'api-documentation',
          name: 'API documentation',
          url: '/docs/',
        },
        {
          id: 'libraries',
          name: 'Libraries',
          url: '/docs/api/libraries/',
        },
        {
          id: 'github',
          name: 'GitHub',
          url: 'https://github.com/plaid',
        },
        {
          id: 'link-demo',
          name: 'Product demos',
          url: '/demo/',
        },
      ],
    },
    aboutUs: {
      header: 'About us',
      items: [
        {
          id: 'company',
          name: 'Company',
          url: '/company/',
        },
        {
          id: 'careers',
          name: 'Careers',
          url: '/careers/',
        },
        {
          id: 'contact',
          name: 'Contact',
          url: '/contact/',
        },
        {
          id: 'partners',
          name: 'Partners',
          url: '/partners/',
        },
        {
          id: 'press',
          name: 'Press',
          url: '/press/',
        },
        {
          id: 'safety',
          name: 'Safety',
          url: '/safety/',
        },
        {
          id: 'legal',
          name: 'Legal',
          url: '/legal/#privacy-statement',
        },
      ],
    },
    resources: {
      header: 'Resources',
      items: [
        {
          id: 'pricing',
          name: 'Pricing',
          url: '/pricing/',
          trackingId: NAVIGATION_EVENTS.FOOTER.USER_CLICKED_FOOTER_NAV_PRICING,
        },
        {
          id: 'global-coverage',
          name: 'Global coverage',
          url: '/global/',
        },
        {
          id: 'plaid-blog',
          name: 'Plaid Blog',
          url: '/blog/',
        },
        {
          id: 'industry-resources',
          name: 'Industry resources',
          url: '/resources/',
        },
        {
          id: 'annual-conference',
          name: 'Annual conference',
          url: '/events/effects/',
        },
        {
          id: 'customer-stories',
          name: 'Customer stories',
          url: '/customer-stories/',
        },
        {
          id: 'intelligent-finance',
          name: 'Intelligent finance',
          url: '/intelligent-finance/',
        },
      ],
    },
    forConsumers: {
      header: 'For consumers',
      items: [
        {
          id: 'troubleshoot-connection',
          name: 'Troubleshoot account connection',
          url: '/trouble-connecting/',
        },
        {
          id: 'trust-safety',
          name: 'Trust and safety',
          url: '/trust-safety/',
        },
        {
          id: 'how-it-works-consumers',
          name: 'How it works',
          url: '/how-it-works-for-consumers/',
        },
        {
          id: 'plaid-portal',
          name: 'Plaid Portal',
          url: '//my.plaid.com',
        },
        {
          id: 'delete-data',
          name: 'Delete my data',
          url: '//my.plaid.com/data-subject-request-form',
        },
        {
          id: 'end-user-privacy-policy',
          name: 'End User Privacy Policy',
          url: '/legal/#end-user-privacy-policy',
        },
        {
          id: 'biometric-policy',
          name: 'Biometric Policy',
          url: '/legal/#biometric-policy-and-release',
        },
        {
          id: 'faqs',
          name: 'FAQs',
          url: '//my.plaid.com/help',
        },
        {
          id: 'plaid-check-consumer-help-center',
          name: 'Plaid Check Consumer Help Center',
          url: '/check/consumer-service-center/',
        },
      ],
    },
    forInstitutions: {
      header: 'For financial institutions',
      items: [
        {
          id: 'open-finance-solution',
          name: 'Open Finance Solution',
          url: '/use-cases/open-finance/',
        },
        {
          id: 'core-exchange',
          name: 'Core Exchange',
          url: '/products/core-exchange/',
        },
        {
          id: 'permissions-manager',
          name: 'Permissions Manager',
          url: '/products/permissions-manager/',
        },
        {
          id: 'app-directory',
          name: 'App Directory',
          url: '/products/app-directory/',
        },
      ],
    },
  },
};
